import intersection from 'lodash.intersection';
import { useEffect, useState } from 'react';

import { STUDENT_MIDDLE_SCHOOL_VIEW_ASSETS_BKS } from 'src/constants/assets.constant';
import {
  ANNE_ARUNDEL_ORG_DEFAULT_GRADE,
  DEFAULT_GRADE,
} from 'src/constants/grades.constants';
import { ANNE_ARUNDEL_ORG_SOURCED_ID } from 'src/constants/onboarding.constants';
import { useActiveUserClasses } from 'src/hooks/use-active-user-classes';
import { useIsMClassUserCheck } from 'src/hooks/use-is-mclass-user-check';
import { useOnboardingUserInfo } from 'src/hooks/use-onboarding-user-info';
import { useUserAssets } from 'src/hooks/use-user-assets.hook';
import { useUserOrgs } from 'src/hooks/use-user-orgs';
import { GRADES } from 'src/interfaces/grades.types';
import AuthService from 'src/services/api/auth.service';
import { useGetSchoolsAndGradesMESQuery } from 'src/store/api/entryways';
import {
  calculateUserGradeClassLevel,
  calculateUserGradeOrgLevel,
} from 'src/utils/grades.utils';
import { isEmpty } from 'src/utils/is-empty';
import { isOrgMember } from 'src/utils/onboarding.utils';

export const useUserGrade = () => {
  const [userGrade, setUserGrade] = useState<GRADES>();

  const { isMClassUser, isLoading: isMclassUserCheckLoading } =
    useIsMClassUserCheck();

  const isTeacher = AuthService.isTeacher();

  const {
    data: userAssets,
    isLoadingWithoutError: isUserAssetsLoading,
    isError: isUserAssetsError,
  } = useUserAssets();

  const { data: onboardingUserInfo, isLoading: isOnboardingUserInfoLoading } =
    useOnboardingUserInfo();
  const { activeUserClasses, isLoading: isUserClassesLoading } =
    useActiveUserClasses();
  const { isLoading: isEntrywaysGradesLoading, data: mESGradesData } =
    useGetSchoolsAndGradesMESQuery(undefined, {
      skip: !isMClassUser,
    });
  const { activeOrgs, isLoading: isUserOrgsLoading } = useUserOrgs();

  const isTeacherGradesDataLoading =
    // Only wait for user classes if assets haven't failed (prevents cascade loading issues)
    (!isUserAssetsError && isUserClassesLoading) ||
    isEntrywaysGradesLoading ||
    isMclassUserCheckLoading ||
    isUserOrgsLoading;

  const isStudentGradesDataLoading =
    isUserAssetsLoading ||
    isOnboardingUserInfoLoading ||
    isUserClassesLoading ||
    isEntrywaysGradesLoading ||
    isMclassUserCheckLoading;

  const isLoading = isTeacher
    ? isTeacherGradesDataLoading
    : isStudentGradesDataLoading;

  // define student user grade by checking assets, onboarding, all classes, mES data
  useEffect(() => {
    if (isTeacher || isStudentGradesDataLoading) return;

    // Only wait for user assets if they haven't failed yet
    if (!isUserAssetsError && !userAssets) return;

    // Skip user assets logic if they failed, go straight to fallbacks
    if (!isUserAssetsError && userAssets.length) {
      const userAssetsBks = userAssets.map((asset) => asset.businessKey);
      const middleSchoolViewAssets = intersection(
        userAssetsBks,
        STUDENT_MIDDLE_SCHOOL_VIEW_ASSETS_BKS,
      );

      if (middleSchoolViewAssets.length) {
        setUserGrade(GRADES.SIXTH);
        return;
      }
    }

    // wait for onboarding user info load
    if (!onboardingUserInfo) return;

    const orgLevelGrades = onboardingUserInfo.grades ?? [];

    if (orgLevelGrades.length) {
      const studentGrade = calculateUserGradeOrgLevel({
        grades: orgLevelGrades,
      });

      setUserGrade(studentGrade);
      return;
    }

    // wait for onboarding user info load
    if (!activeUserClasses) return;

    const hasNoGradeRelatedData =
      isEmpty(activeUserClasses) && isEmpty(mESGradesData);

    if (
      hasNoGradeRelatedData &&
      isOrgMember(ANNE_ARUNDEL_ORG_SOURCED_ID, activeOrgs)
    ) {
      setUserGrade(ANNE_ARUNDEL_ORG_DEFAULT_GRADE);

      return;
    }

    if (hasNoGradeRelatedData) {
      setUserGrade(DEFAULT_GRADE);

      return;
    }

    // Set mES grades for mClass user if no data came from the user classes query.
    if (isEmpty(activeUserClasses) && isMClassUser && mESGradesData) {
      setUserGrade(mESGradesData);

      return;
    }

    setUserGrade(() =>
      calculateUserGradeClassLevel(activeUserClasses, activeOrgs),
    );
  }, [
    activeOrgs,
    activeUserClasses,
    isStudentGradesDataLoading,
    isMClassUser,
    isTeacher,
    mESGradesData,
    onboardingUserInfo,
    userAssets,
    isUserAssetsError,
  ]);

  // define teacher user grade by checking all classes, orgs, mES data
  useEffect(() => {
    if (!isTeacher || isTeacherGradesDataLoading || !activeUserClasses) {
      return;
    }

    const hasNoGradeRelatedData =
      isEmpty(activeUserClasses) && isEmpty(mESGradesData);

    if (
      hasNoGradeRelatedData &&
      isOrgMember(ANNE_ARUNDEL_ORG_SOURCED_ID, activeOrgs)
    ) {
      setUserGrade(ANNE_ARUNDEL_ORG_DEFAULT_GRADE);

      return;
    }

    if (hasNoGradeRelatedData) {
      setUserGrade(DEFAULT_GRADE);

      return;
    }

    // Set mES grades for mClass user if no data came from the user classes query.
    if (isEmpty(activeUserClasses) && isMClassUser && mESGradesData) {
      setUserGrade(mESGradesData);

      return;
    }

    setUserGrade(() =>
      calculateUserGradeClassLevel(activeUserClasses, activeOrgs),
    );
  }, [
    activeUserClasses,
    isMClassUser,
    mESGradesData,
    isTeacherGradesDataLoading,
    activeOrgs,
    isTeacher,
  ]);

  return { userGrade, isGradesLoading: isLoading };
};
